import { GearIcon, LightningIcon, PlusIcon } from '@phosphor-icons/react'
import { ColumnDef, ColumnHelper, createColumnHelper, Row } from '@tanstack/react-table'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { Routes } from '../../routes'
import {
  useAdminRole,
  useGetLatestAnalysisForRepository,
  useGetRepositoryIntegrations,
  useGetWorkflows,
} from '../../utils/api-client/api-hooks'
import { getRepositoryProject, type Repository, type Tool } from '../../utils/api-client/api-schemas'
import { convertIntegrationTypeToTool } from '../../utils/repository-utils.tsx'
import type { DrawerContent } from '../drawers/workflow-drawer'

import classNames from 'classnames'
import { LatestAnalysisLink } from '../latest-analysis-link'
import { SyncLatestDropdown } from '../menus/sync-latest-dropdown'
import { ProjectChip } from '../projects/project-chip.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon'
import { TooltipWithHover } from '../tooltip'
import * as styles from './repositories-table.css'

export const RepositoriesTableLayoutSchema = z.enum(['plain', 'beacon', 'risk-and-beacon'])
/**
 * Which columns share the row. The base three (repository, latest scan, actions) are always there; a
 * route splices in the rest — Pixee Beacon with Labs on, and the risk column once enough repositories
 * are scored, which also needs Labs.
 */
export type RepositoriesTableLayout = z.infer<typeof RepositoriesTableLayoutSchema>

type ColumnShares = { repository: number; risk?: number; beacon?: number; analysis: number; actions: number }

/**
 * Every column's share of the row, per layout. `tableLayout: 'fixed'` is unconditional, and under it a
 * column with no width does not size to content — the browser splits the space equally — so each layout
 * declares every column, and each sums to 100% (the column tests hold them to it). One table rather than
 * a constant per spliced column, because adding a column means rebalancing the others.
 *
 * - plain: a repository name below ~31% wraps to two lines at 1120px; analysis and actions keep room.
 * - beacon: the cell holds a compact split button or a pill, ~200px.
 * - risk-and-beacon: risk is the widest extra, it carries a donut, a sentence and an evidence line.
 */
export const COLUMN_WIDTH_PERCENT: Record<RepositoriesTableLayout, ColumnShares> = {
  plain: { repository: 55, analysis: 30, actions: 15 },
  beacon: { repository: 38, beacon: 24, analysis: 26, actions: 12 },
  'risk-and-beacon': { repository: 28, risk: 27, beacon: 19, analysis: 16, actions: 10 },
}

/** A column's width in a layout. A spliced column is only ever asked for in a layout that has it. */
export const columnWidth = (layout: RepositoriesTableLayout | undefined, column: keyof ColumnShares): string =>
  `${COLUMN_WIDTH_PERCENT[layout ?? 'plain'][column] ?? 0}%`

/**
 * The layout whose widths fit the columns a route splices in, so the base columns' widths cannot disagree
 * with the columns actually shown. A set of columns no layout declares throws.
 */
export const layoutOfExtraColumns = (columns: readonly ColumnDef<Repository>[] | undefined): RepositoriesTableLayout =>
  match((columns ?? []).map(column => column.id).join(','))
    .with('', (): RepositoriesTableLayout => 'plain')
    .with('beacon', (): RepositoriesTableLayout => 'beacon')
    .with('risk,beacon', (): RepositoriesTableLayout => 'risk-and-beacon')
    .otherwise(ids => {
      throw new Error(`No repositories table layout for the columns: ${ids}`)
    })

export type UseRepositoriesTableColumnDefinitionsOptions = Omit<
  Parameters<typeof createRepositoryColumnDefinition>[0] &
    Parameters<typeof createAnalysisColumnDefinition>[0] &
    Parameters<typeof createActionsColumnDefinition>[0],
  'columnHelper'
>

export const useRepositoriesTableColumnDefinitions = ({
  setSelectedWorkflowRepositoryId,
  setSelectedSettingsRepositoryId,
  setWorkflowDrawerContent,
  onAddFindings,
  onSyncTool,
  layout = 'plain',
}: UseRepositoriesTableColumnDefinitionsOptions) => {
  return useMemo(() => {
    const columnHelper = createColumnHelper<Repository>()

    return [
      createRepositoryColumnDefinition({
        columnHelper,
        setSelectedWorkflowRepositoryId,
        setWorkflowDrawerContent,
        layout,
      }),
      createAnalysisColumnDefinition({ columnHelper, onAddFindings, layout }),
      createActionsColumnDefinition({
        columnHelper,
        setSelectedWorkflowRepositoryId,
        setSelectedSettingsRepositoryId,
        setWorkflowDrawerContent,
        onAddFindings,
        onSyncTool,
        layout,
      }),
    ] satisfies ColumnDef<Repository>[]
  }, [
    setSelectedWorkflowRepositoryId,
    setSelectedSettingsRepositoryId,
    setWorkflowDrawerContent,
    onAddFindings,
    onSyncTool,
    layout,
  ])
}

const RepositoryInfo: React.FC<{ repository: Pick<Repository, 'type' | 'full_name'> }> = ({ repository }) => {
  return (
    <span className={styles.repositoryName}>
      <div style={{ marginRight: '12px' }}>
        <SCMIcon type={repository.type} size="medium" />
      </div>
      {repository.full_name}
    </span>
  )
}

// #region Repository column

const createRepositoryColumnDefinition = ({
  columnHelper,
  setSelectedWorkflowRepositoryId,
  setWorkflowDrawerContent,
  layout,
}: {
  columnHelper: ColumnHelper<Repository>
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
  /** Which columns share the row; see {@link COLUMN_WIDTH_PERCENT}. */
  layout?: RepositoriesTableLayout
}) => {
  return columnHelper.accessor(row => row.full_name, {
    id: 'repository',
    meta: { width: columnWidth(layout, 'repository') },
    cell: ({ row }) => (
      <RepositoryColumnCell
        row={row}
        setSelectedWorkflowRepositoryId={setSelectedWorkflowRepositoryId}
        setWorkflowDrawerContent={setWorkflowDrawerContent}
      />
    ),
    header: RepositoryColumnHeader,
  }) as ColumnDef<Repository>
}

const RepositoryColumnHeader: React.FC = () => <span>REPOSITORY</span>

const RepositoryColumnCell: React.FC<{
  row: Row<Repository>
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
}> = ({ row, setSelectedWorkflowRepositoryId, setWorkflowDrawerContent }) => {
  const { data: workflowsData } = useGetWorkflows({ repositoryId: row.original.id })
  const workflowCount = workflowsData?.total ?? 0
  const project = getRepositoryProject(row.original)

  const handleLightningClick = () => {
    setSelectedWorkflowRepositoryId(row.original.id)
    if (setWorkflowDrawerContent) {
      setWorkflowDrawerContent('manage')
    }
  }

  return (
    <div className={styles.repositoryNameContainer}>
      <Link
        to={Routes.RepositoryDetailsPage.createPath({ repositoryId: row.original.id })}
        className={styles.repositoryNameLink}
      >
        <RepositoryInfo repository={row.original} />
      </Link>
      {project && <ProjectChip name={project.name} />}
      {workflowCount > 0 && (
        <TooltipWithHover
          trigger={
            <button className={styles.workflowButton} aria-label="Workflows" onClick={handleLightningClick}>
              <LightningIcon size={16} weight="fill" className={styles.workflowIndicator} />
            </button>
          }
          size="small"
        >
          <span>
            Manage {workflowCount} {workflowCount === 1 ? 'workflow' : 'workflows'}
          </span>
        </TooltipWithHover>
      )}
    </div>
  )
}
// #endregion Repository column

// #region Analysis column

type CreateAnalysisColumnDefinitionOptions = {
  columnHelper: ColumnHelper<Repository>
  onAddFindings: (repositoryId: string) => unknown
  /** Which columns share the row; see {@link COLUMN_WIDTH_PERCENT}. */
  layout?: RepositoriesTableLayout
}

const createAnalysisColumnDefinition = ({
  columnHelper,
  onAddFindings,
  layout,
}: CreateAnalysisColumnDefinitionOptions) =>
  columnHelper.display({
    id: 'analysis',
    meta: { width: columnWidth(layout, 'analysis') },
    cell: AnalysisColumnCell({ onAddFindings }),
    header: AnalysisColumnHeader,
  })

const AnalysisColumnHeader: React.FC = () => <span className={styles.center}>LATEST SCAN WITH ANALYSIS</span>

const AnalysisColumnCell: ({
  onAddFindings,
}: Pick<CreateAnalysisColumnDefinitionOptions, 'onAddFindings'>) => React.FC<{
  row: Row<Repository>
}> =
  ({ onAddFindings }) =>
  ({ row }) => {
    const { data: latestAnalysis } = useGetLatestAnalysisForRepository({ repositoryId: row.original.id })

    return (
      <div className={styles.center}>
        {latestAnalysis ? (
          <LatestAnalysisLink latestAnalysis={latestAnalysis} />
        ) : (
          <a
            href="#"
            className={styles.uploadScanLink}
            onClick={e => {
              e.preventDefault()
              onAddFindings(row.original.id)
            }}
          >
            Add findings
            <PlusIcon size={15} className={styles.addFindingsIcon} />
          </a>
        )}
      </div>
    )
  }

// #endregion Analysis column

// #region Actions column

type CreateActionsColumnDefinitionOptions = ActionsColumnCellFactoryOptions & {
  columnHelper: ColumnHelper<Repository>
  /** Which columns share the row; see {@link COLUMN_WIDTH_PERCENT}. */
  layout?: RepositoriesTableLayout
}

const createActionsColumnDefinition = ({
  columnHelper,
  setSelectedWorkflowRepositoryId,
  setSelectedSettingsRepositoryId,
  setWorkflowDrawerContent,
  onAddFindings,
  onSyncTool,
  layout,
}: CreateActionsColumnDefinitionOptions) =>
  columnHelper.display({
    id: 'actions',
    meta: { width: columnWidth(layout, 'actions') },
    cell: ActionsColumnCell({
      setSelectedWorkflowRepositoryId,
      setSelectedSettingsRepositoryId,
      setWorkflowDrawerContent,
      onAddFindings,
      onSyncTool,
    }),
    header: ActionsColumnHeader,
  })

const ActionsColumnHeader: React.FC = () => <span>Actions</span>

type ActionsColumnCellFactoryOptions = {
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setSelectedSettingsRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
  onAddFindings: (repositoryId: string) => unknown
  onSyncTool: (repositoryId: string, tool: Tool, repositoryName: string) => unknown
}

const ActionsColumnCell: (options: ActionsColumnCellFactoryOptions) => React.FC<{ row: Row<Repository> }> =
  ({
    setSelectedWorkflowRepositoryId,
    setSelectedSettingsRepositoryId,
    setWorkflowDrawerContent,
    onAddFindings,
    onSyncTool,
  }) =>
  ({ row }) => {
    const [isSyncDropdownOpen, setIsSyncDropdownOpen] = useState(false)
    // Same query as the scan column, so it is served from that cell's cache rather than fetched again.
    const { data: latestAnalysis } = useGetLatestAnalysisForRepository({ repositoryId: row.original.id })
    const { data: integrations } = useGetRepositoryIntegrations({ repositoryId: row.original.id })
    const mappedTools: Tool[] = useMemo(
      () => [
        ...new Set(
          (integrations?._embedded?.items ?? []).map(integration => convertIntegrationTypeToTool(integration.type))
        ),
      ],
      [integrations]
    )

    return (
      <div
        className={classNames(styles.center, styles.actionsContainer)}
        data-dropdown-open={isSyncDropdownOpen || undefined}
      >
        {/* With no scan yet, the scan column's "Add findings" is this action, so a second one would repeat it. */}
        {latestAnalysis && <AddFindingsActionButton onAddFindings={onAddFindings} row={row} />}
        <SyncLatestDropdown
          repositoryId={row.original.id}
          mappedTools={mappedTools}
          onSyncTool={tool => onSyncTool(row.original.id, tool, row.original.name)}
          triggerClassName={styles.actionButton}
          onOpenChange={setIsSyncDropdownOpen}
        />
        <ManageWorkflowsActionButton
          setSelectedWorkflowRepositoryId={setSelectedWorkflowRepositoryId}
          setWorkflowDrawerContent={setWorkflowDrawerContent}
          row={row}
        />
        <RepositorySettingsActionButton setSelectedSettingsRepositoryId={setSelectedSettingsRepositoryId} row={row} />
      </div>
    )
  }

const AddFindingsActionButton: React.FC<{
  row: Row<Repository>
  onAddFindings: (repositoryId: string) => unknown
}> = ({ onAddFindings, row }) => {
  return (
    <TooltipWithHover
      trigger={
        <button
          className={styles.actionButton}
          onClick={() => onAddFindings(row.original.id)}
          aria-label="Add findings for analysis"
        >
          <PlusIcon size={15} weight="light" />
        </button>
      }
      size="small"
    >
      <span>Add findings for analysis</span>
    </TooltipWithHover>
  )
}

const ManageWorkflowsActionButton: React.FC<{
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  setWorkflowDrawerContent?: React.Dispatch<React.SetStateAction<DrawerContent>>
  row: Row<Repository>
}> = ({ setSelectedWorkflowRepositoryId, setWorkflowDrawerContent, row }) => {
  const { hasWriteAccess } = useAdminRole()
  const handleClick = () => {
    setSelectedWorkflowRepositoryId(row.original.id)
    if (setWorkflowDrawerContent) {
      setWorkflowDrawerContent('create')
    }
  }

  // Creating a workflow is admin-only on the platform (ISS-8480). The lightning button on the
  // repository column opens the same drawer in its read-only `manage` view and is untouched, so a
  // non-admin can still see what workflows a repository has.
  //
  // `useAdminRole` explains why a failed role lookup still offers the control; this cell is the
  // surface with the most to lose from the alternative. It is a row of icon buttons with nowhere
  // to put the "Unable to confirm your permissions" sentence the organization page uses, and on a
  // repository with no workflows the lightning button is absent too (it needs `workflowCount > 0`),
  // so withholding would leave the row with no workflow affordance at all and nothing saying why.
  if (!hasWriteAccess) {
    return null
  }

  return (
    <TooltipWithHover
      trigger={
        <button className={styles.actionButton} onClick={handleClick} aria-label="Create workflow">
          <LightningIcon size={15} weight="light" />
        </button>
      }
      size="small"
    >
      <span>Create workflow</span>
    </TooltipWithHover>
  )
}

const RepositorySettingsActionButton: React.FC<{
  setSelectedSettingsRepositoryId: React.Dispatch<React.SetStateAction<string | undefined>>
  row: Row<Repository>
}> = ({ setSelectedSettingsRepositoryId, row }) => {
  return (
    <TooltipWithHover
      trigger={
        <button
          className={styles.actionButton}
          onClick={() => setSelectedSettingsRepositoryId(row.original.id)}
          aria-label="Repository settings"
        >
          <GearIcon size={15} weight="light" />
        </button>
      }
      size="small"
    >
      <span>Repository settings</span>
    </TooltipWithHover>
  )
}

// #endregion Actions column
